import React, { useMemo, useReducer } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment'
import DayPicker from 'react-day-picker'
import Loader from 'react-loader-spinner'
import { GlgPopup } from 'SharedComponents'
import {
  INITIAL_STATE,
  MAX_ROUNDS,
  addAnotherRound,
  canAddRound,
  closeDayDialog,
  dayClicked,
  removeRound,
  reset,
  roundDaysReducer,
  roundsOnDay,
  sortedDays,
} from './reducer'

const SUBMIT_DATE_FORMAT = 'DD/MM/YYYY'

const internationalize = (key, data = null) => !window.I18n ? '' : window.I18n.t(key, data)

const localizedCalendarProps = () => {
  const monthNames = internationalize('date.month_names')
  const dayNames = internationalize('date.day_names')
  const minDayNames = internationalize('date.min_day_names')
  if (!Array.isArray(monthNames) || !Array.isArray(dayNames) || !Array.isArray(minDayNames)) { return {} }

  return {
    months: monthNames.filter(month => month !== null),
    weekdaysLong: dayNames,
    weekdaysShort: minDayNames,
    firstDayOfWeek: parseInt(internationalize('jquery_datepicker.firstDay'), 10) || 0,
  }
}

const formatDayDialogDate = (day) => {
  const dayNames = internationalize('date.day_names')
  const monthNames = internationalize('date.month_names')
  if (!Array.isArray(dayNames) || !Array.isArray(monthNames)) {
    return moment(day).format('dddd, MMMM D')
  }

  return internationalize('dashboard_plus.widgets.day_popup_date', {
    day_name: dayNames[day.getDay()],
    month_name: monthNames[day.getMonth() + 1],
    day_number: day.getDate(),
  })
}

const activateOnKey = (handler) => (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') { return }

  event.preventDefault()
  handler()
}

const renderDayCircle = (day) => <span className='day-circle'>{ day.getDate() }</span>

const SelectedCount = ({ count }) => (
  <label className='font-weight-normal selected-count'>
    { internationalize('dashboard_plus.widgets.selected_rounds_count', { count, max: MAX_ROUNDS }) }
  </label>
)

SelectedCount.propTypes = {
  count: PropTypes.number.isRequired,
}

const DayDialog = ({ day, roundsCount, canAddMore, onAddAnother, onRemove, onClose }) => (
  <GlgPopup
    show
    modalClass='calendar-round-adder calendar-round-adder-day-popup'
    title={ internationalize('dashboard_plus.widgets.rounds_on_day', { count: roundsCount }) }
    saveButtonText={ internationalize('dashboard_plus.widgets.add_another_round') }
    saveButtonEnabled={ canAddMore }
    closeButtonText={ internationalize('buttons.cancel') }
    footerContent={
      <a
        className='day-popup-delete'
        role='button'
        tabIndex={ 0 }
        onClick={ onRemove }
        onKeyDown={ activateOnKey(onRemove) }>
        { internationalize('buttons.delete') }
      </a>
    }
    onClose={ onClose }
    onSave={ onAddAnother }>
    <div className='day-popup-date'>{ formatDayDialogDate(day) }</div>
  </GlgPopup>
)

DayDialog.propTypes = {
  day: PropTypes.instanceOf(Date).isRequired,
  roundsCount: PropTypes.number.isRequired,
  canAddMore: PropTypes.bool.isRequired,
  onAddAnother: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}

// Multi-date calendar used to add one or more rounds in a single request.
// Rendered wide and inline on the empty rounds widget, and inside a popup
// (triggered by "+ Add round") once rounds exist. Modeled on the
// event-clone dates picker. Selection state lives in ./reducer.
const CalendarRoundAdder = ({ lastPossibleDay, adding, addRounds, popup, show, onClose }) => {
  const [ { selectedDays, dayDialog }, dispatch ] = useReducer(roundDaysReducer, INITIAL_STATE)
  const calendarProps = useMemo(localizedCalendarProps, [])

  const handleDayClick = (day, modifiers = {}) => {
    if (modifiers.disabled) { return }

    dispatch(dayClicked(day))
  }

  const handleAddClick = () => {
    if (selectedDays.length === 0 || adding) { return }

    addRounds(sortedDays(selectedDays).map(day => moment(day).format(SUBMIT_DATE_FORMAT)))
  }

  const handleClose = () => {
    dispatch(reset())
    onClose()
  }

  const disabledDays = [ { before: new Date() } ]
  if (lastPossibleDay) { disabledDays.push({ after: moment.unix(lastPossibleDay).toDate() }) }

  const calendarBody = (
    <div className='calendar-body'>
      <DayPicker
        numberOfMonths={ popup ? 2 : 3 }
        selectedDays={ selectedDays }
        modifiers={{ multiRound: day => roundsOnDay(selectedDays, day) >= 2 }}
        renderDay={ renderDayCircle }
        disabledDays={ disabledDays }
        onDayClick={ handleDayClick }
        { ...calendarProps }
      />
      {
        dayDialog &&
          <DayDialog
            day={ dayDialog }
            roundsCount={ roundsOnDay(selectedDays, dayDialog) }
            canAddMore={ canAddRound(selectedDays) }
            onAddAnother={ () => dispatch(addAnotherRound()) }
            onRemove={ () => dispatch(removeRound()) }
            onClose={ () => dispatch(closeDayDialog()) }
          />
      }
    </div>
  )

  if (popup) {
    return (
      <GlgPopup
        show={ show }
        size='lg'
        modalClass='calendar-round-adder add-rounds-popup'
        title={ internationalize('dashboard_plus.widgets.add_rounds_button') }
        saveButtonText={ internationalize('dashboard_plus.widgets.add_rounds_button') }
        closeButtonText={ internationalize('buttons.cancel') }
        saveButtonEnabled={ selectedDays.length > 0 && !adding }
        isSaving={ adding }
        onClose={ handleClose }
        onSave={ handleAddClick }>
        { calendarBody }
        <SelectedCount count={ selectedDays.length } />
      </GlgPopup>
    )
  }

  const nothingSelected = selectedDays.length === 0

  return (
    <div className='calendar-round-adder empty-rounds-calendar'>
      { calendarBody }
      <div className='calendar-actions'>
        <SelectedCount count={ selectedDays.length } />
        {
          adding ?
            <Loader type='Oval' color={ '#00A0CE' } height={ 30 } width={ 30 } />
            :
            <a
              className={ `save-button add-rounds-button ${nothingSelected ? 'disabled' : ''}` }
              role='button'
              tabIndex={ 0 }
              aria-disabled={ nothingSelected }
              onClick={ handleAddClick }
              onKeyDown={ activateOnKey(handleAddClick) }>
              + { internationalize('dashboard_plus.widgets.add_rounds_button') }
            </a>
        }
      </div>
    </div>
  )
}

CalendarRoundAdder.propTypes = {
  lastPossibleDay: PropTypes.number,
  adding: PropTypes.bool,
  addRounds: PropTypes.func.isRequired,
  popup: PropTypes.bool,
  show: PropTypes.bool,
  onClose: PropTypes.func,
}

CalendarRoundAdder.defaultProps = {
  adding: false,
  popup: false,
  show: false,
  onClose: () => {},
}

export default CalendarRoundAdder
